import {
  IncidentActionType,
  IncidentSource,
  IncidentStatus,
} from '@/graphql/generated/schemas'
import { formatTimeDistance } from '@/utils/formatDateTime'
import { getIncidentSourceDisplayName } from '@/utils/incidents'

import { IncidentRowDataType, IncidentsListType } from '../types/types'

//The 230px here are the Header, IncidentsSearch and padding height values
export const INCIDENT_TABLE_HEIGHT = 'calc(100vh - 230px)'

/**
 * The legacy bulk-resolve mutation is all-or-nothing and accepts ACTIVE
 * incidents only. Every selected ID must still have a corresponding record so
 * a row removed by polling cannot silently fall out of the eligibility check.
 * Keep this rule in one place so the legacy and rebranded tables cannot drift
 * from the server contract.
 */
export const canBulkResolveIncidents = (
  incidents: Array<Pick<IncidentsListType, 'status'>>,
  selectedIncidentCount: number
): boolean =>
  selectedIncidentCount > 0 &&
  incidents.length === selectedIncidentCount &&
  incidents.every(({ status }) => status === IncidentStatus.Active)

export const getTimeSinceDispatched = (incident: IncidentsListType): string => {
  const sortedAudioMessages = [...(incident?.audioMessages?.edges ?? [])].sort(
    (a, b) =>
      new Date(a?.node?.createdAt).valueOf() -
      new Date(b?.node?.createdAt).valueOf()
  )
  const dispatchTime =
    sortedAudioMessages && sortedAudioMessages[0]?.node?.createdAt
  const timeSinceDispatch = formatTimeDistance(dispatchTime)
  return timeSinceDispatch ? `Dispatched: ${timeSinceDispatch}` : ''
}

export const getIsReleased = (
  incident: IncidentsListType,
  hasGuard: boolean
): boolean => {
  return (
    incident?.actions?.some(
      (e) => e.type === IncidentActionType.ReleaseByGuard
    ) && !hasGuard
  )
}

export const formatTableData = (
  incidents: IncidentsListType[],
  isQuickReportWhiteListEnabled = false
): IncidentRowDataType[] => {
  return incidents.map((inc) => {
    const device = inc?.devices[0]
    return {
      id: inc?.id,
      isFlagged: inc?.isFlagged,
      priority: inc?.priority,
      displayId: inc?.displayId,
      facility: {
        name: inc?.facility?.name,
        shortName: inc?.facility?.shortName,
        archived: inc?.facility?.archived,
      },
      incidentName:
        isQuickReportWhiteListEnabled &&
        inc?.source === IncidentSource.QuickReport
          ? 'QuickReport Incident'
          : inc?.name,
      floorName: inc?.floor?.name,
      date: inc?.createdAt,
      incidentStatusOwner: {
        status: inc?.status,
        isFlagged: inc?.isFlagged,
        hasVideoClip: inc?.hasVideoClip,
        owner: {
          firstName: inc?.owner?.firstName,
          lastName: inc?.owner?.lastName,
        },
      },
      incidentType: inc?.type,
      source: device?.type
        ? { name: device?.name, type: device?.type }
        : {
            name: getIncidentSourceDisplayName({
              name: '',
              type: inc?.source,
            }),
            type: inc?.source,
          },
      operator: inc?.owner
        ? `${inc?.owner?.firstName} ${inc?.owner?.lastName}`
        : null,
      guard: inc,
    }
  })
}
